import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, ChevronUp, ChevronDown, Film, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CATS = ["Ön Muhasebe", "Genel Muhasebe", "Beyanname", "İleri Seviye"];
const LEVELS = ["Başlangıç", "Orta", "İleri"];

const empty = {
  title: "", slug: "", category: "Ön Muhasebe", level: "Başlangıç",
  short_description: "", description: "", duration: "10 saat",
  price: 1000, currency: "TRY", image_url: "https://images.unsplash.com/photo-1706074793638-da28b90ea8ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  shopier_url: "", youtube_preview_id: "", payment_ref_code: "",
  google_meet_url: "", virtual_windows_url: "", schedule_text: "",
  curriculum: [], outcomes: [], featured: false, published: true,
};

export default function AdminCourses() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/courses").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...empty, _new: true }); setErr(""); };
  const openEdit = (c) => { setEditing({ ...c, _new: false }); setErr(""); };
  const close = () => setEditing(null);

  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing };
      delete payload._new; delete payload.id; delete payload.created_at;
      payload.price = Number(payload.price);
      if (editing._new) await api.post("/admin/courses", payload);
      else await api.put(`/admin/courses/${editing.id}`, payload);
      await load();
      close();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu eğitimi silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/courses/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="section-eyebrow">Yönetim</span>
          <h1 className="font-display text-4xl mt-2">Eğitimler</h1>
        </div>
        <button data-testid="new-course-btn" onClick={openNew} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /> Yeni Eğitim
        </button>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-white/60 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">Başlık</th>
              <th className="text-left p-4">Kategori</th>
              <th className="text-left p-4">Fiyat</th>
              <th className="text-left p-4">Öne Çıkan</th>
              <th className="text-left p-4">Yayın</th>
              <th className="text-right p-4">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="p-4">
                  <div className="font-medium text-white">{c.title}</div>
                  <div className="text-xs text-white/40 mt-1">{c.slug}</div>
                </td>
                <td className="p-4 text-white/70">{c.category}</td>
                <td className="p-4 text-gold font-display">₺{new Intl.NumberFormat("tr-TR").format(c.price)}</td>
                <td className="p-4">{c.featured ? <span className="text-gold text-xs">★ Öne Çıkan</span> : <span className="text-white/40 text-xs">—</span>}</td>
                <td className="p-4">{c.published ? <span className="text-green-400 text-xs">Yayında</span> : <span className="text-white/40 text-xs">Taslak</span>}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(c)} className="text-white/60 hover:text-gold p-2" data-testid={`edit-course-${c.slug}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(c.id)} className="text-white/60 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-white/50">Henüz eğitim yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start justify-center p-4 md:p-10 overflow-y-auto">
          <div className="w-full max-w-4xl bg-surface border border-gold-30 p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl">{editing._new ? "Yeni Eğitim" : "Eğitimi Düzenle"}</h2>
              <button onClick={close} className="text-white/60 hover:text-gold"><X className="w-6 h-6" /></button>
            </div>
            {err && <div className="bg-red-950/40 border border-red-800 text-red-200 p-3 text-sm mb-4">{err}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Başlık"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="admin-input" /></Field>
              <Field label="Slug (URL)"><input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="admin-input" /></Field>
              <Field label="Kategori">
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="admin-input">
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Seviye">
                <select value={editing.level} onChange={(e) => setEditing({ ...editing, level: e.target.value })} className="admin-input">
                  {LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Süre"><input value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} className="admin-input" /></Field>
              <Field label="Fiyat (₺)"><input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="admin-input" /></Field>
              <Field label="Kapak Görseli URL" span2><input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="admin-input" /></Field>
              <Field label="Shopier Satın Alma URL" span2><input value={editing.shopier_url} onChange={(e) => setEditing({ ...editing, shopier_url: e.target.value })} className="admin-input" placeholder="https://www.shopier.com/..." /></Field>
              <Field label="Havale/EFT Açıklama Kodu (sabit)"><input value={editing.payment_ref_code} onChange={(e) => setEditing({ ...editing, payment_ref_code: e.target.value })} className="admin-input" placeholder="Örn: ÖM-123" /></Field>
              <Field label="YouTube Video ID (ücretsiz ders)" span2><input value={editing.youtube_preview_id} onChange={(e) => setEditing({ ...editing, youtube_preview_id: e.target.value })} className="admin-input" placeholder="Örn: dQw4w9WgXcQ" /></Field>
              <Field label="Google Meet Linki (üyelere özel, canlı ders)" span2><input value={editing.google_meet_url} onChange={(e) => setEditing({ ...editing, google_meet_url: e.target.value })} className="admin-input" placeholder="https://meet.google.com/xxx-xxxx-xxx" /></Field>
              <Field label="Sanal Windows İndirme Linki (üyelere özel, Drive linki)" span2><input value={editing.virtual_windows_url} onChange={(e) => setEditing({ ...editing, virtual_windows_url: e.target.value })} className="admin-input" placeholder="https://drive.google.com/..." /></Field>
              <Field label="Ders Programı (üyelere özel, serbest metin)" span2><textarea rows={3} value={editing.schedule_text} onChange={(e) => setEditing({ ...editing, schedule_text: e.target.value })} className="admin-input" placeholder="Örn: Salı 20:00 - Canlı Ders&#10;Perşembe 20:00 - Soru Cevap" /></Field>
              <Field label="Kısa Açıklama" span2><textarea rows={2} value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} className="admin-input" /></Field>
              <Field label="Detaylı Açıklama" span2><textarea rows={5} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="admin-input" /></Field>
              <Field label="Kazanımlar (her satıra bir madde)" span2>
                <textarea rows={4} value={(editing.outcomes || []).join("\n")} onChange={(e) => setEditing({ ...editing, outcomes: e.target.value.split("\n").filter(Boolean) })} className="admin-input" />
              </Field>
              <Field label="Müfredat — Modüller ve Dersler (Google Drive video linki)" span2>
                <CurriculumEditor
                  curriculum={editing.curriculum || []}
                  onChange={(curriculum) => setEditing({ ...editing, curriculum })}
                />
              </Field>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Öne Çıkan
              </label>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Yayında
              </label>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={close} className="btn-outline text-sm">İptal</button>
              <button onClick={save} data-testid="save-course-btn" className="btn-primary text-sm"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input { width: 100%; background: #0F0F14; border: 1px solid rgba(255,255,255,0.08); padding: 0.6rem 0.9rem; color: white; font-family: 'Outfit', sans-serif; outline: none; font-size: 0.875rem; }
        .admin-input:focus { border-color: rgba(212,175,55,0.6); }
      `}</style>
    </div>
  );
}

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function CurriculumEditor({ curriculum, onChange }) {
  const addModule = () => {
    onChange([...(curriculum || []), { title: `Modül ${curriculum.length + 1}`, lessons: [] }]);
  };
  const updateModule = (mi, patch) => {
    const next = curriculum.map((m, i) => (i === mi ? { ...m, ...patch } : m));
    onChange(next);
  };
  const removeModule = (mi) => onChange(curriculum.filter((_, i) => i !== mi));
  const moveModule = (mi, dir) => {
    const j = mi + dir;
    if (j < 0 || j >= curriculum.length) return;
    const next = [...curriculum];
    [next[mi], next[j]] = [next[j], next[mi]];
    onChange(next);
  };

  const addLesson = (mi) => {
    const m = curriculum[mi];
    const lessons = [...(m.lessons || []), { id: uid(), title: "Yeni Ders", video_url: "", duration: "", document_url: "" }];
    updateModule(mi, { lessons });
  };
  const updateLesson = (mi, li, patch) => {
    const m = curriculum[mi];
    const lessons = (m.lessons || []).map((l, i) => {
      const lesson = typeof l === "string" ? { id: uid(), title: l, video_url: "", duration: "", document_url: "" } : l;
      return i === li ? { ...lesson, ...patch } : lesson;
    });
    updateModule(mi, { lessons });
  };
  const removeLesson = (mi, li) => {
    const m = curriculum[mi];
    updateModule(mi, { lessons: (m.lessons || []).filter((_, i) => i !== li) });
  };

  return (
    <div className="space-y-4 bg-black/20 border border-white/10 p-4">
      {(curriculum || []).map((m, mi) => (
        <div key={mi} className="border border-white/10 bg-surface-elev p-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              value={m.title}
              onChange={(e) => updateModule(mi, { title: e.target.value })}
              className="admin-input flex-1 font-display"
              placeholder="Modül başlığı"
            />
            <button type="button" onClick={() => moveModule(mi, -1)} className="text-white/50 hover:text-gold p-2"><ChevronUp className="w-4 h-4" /></button>
            <button type="button" onClick={() => moveModule(mi, 1)} className="text-white/50 hover:text-gold p-2"><ChevronDown className="w-4 h-4" /></button>
            <button type="button" onClick={() => removeModule(mi)} className="text-white/50 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2">
            {(m.lessons || []).map((lraw, li) => {
              const l = typeof lraw === "string" ? { id: uid(), title: lraw, video_url: "", duration: "", document_url: "" } : lraw;
              return (
                <div key={l.id || li} className="bg-black/30 p-2.5 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_90px_auto] gap-2 items-center">
                    <input
                      value={l.title}
                      onChange={(e) => updateLesson(mi, li, { title: e.target.value })}
                      className="admin-input"
                      placeholder="Ders başlığı"
                    />
                    <div className="relative">
                      <Film className="w-3.5 h-3.5 text-white/30 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        value={l.video_url}
                        onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })}
                        className="admin-input pl-8"
                        placeholder="YouTube veya Drive video linki"
                      />
                    </div>
                    <input
                      value={l.duration}
                      onChange={(e) => updateLesson(mi, li, { duration: e.target.value })}
                      className="admin-input"
                      placeholder="12 dk"
                    />
                    <button type="button" onClick={() => removeLesson(mi, li)} className="text-white/50 hover:text-red-400 p-2 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="relative">
                    <FileText className="w-3.5 h-3.5 text-white/30 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      value={l.document_url || ""}
                      onChange={(e) => updateLesson(mi, li, { document_url: e.target.value })}
                      className="admin-input pl-8"
                      placeholder="Ders dökümanı linki (PDF, Drive vb.)"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={() => addLesson(mi)} className="btn-outline text-xs mt-3">
            <Plus className="w-3.5 h-3.5" /> Ders Ekle
          </button>
        </div>
      ))}
      <button type="button" onClick={addModule} className="btn-primary text-sm">
        <Plus className="w-4 h-4" /> Modül Ekle
      </button>
    </div>
  );
}

function Field({ label, span2, children }) {
  return (
    <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">{label}</span>
      {children}
    </label>
  );
}
